import { useState } from 'react';
import { Plus, Minus, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { FAQItem } from '../lib/faqData';

// Homepage-only questions for commercial low-voltage buyers. Managed IT stays last.
const homepageFAQs: FAQItem[] = [
  {
    question: 'What cabling and fiber work do you do?',
    answer: 'Our own crew installs structured cabling and fiber for businesses: data drops for desks, phones, cameras, access points and door readers, and fiber between rooms, floors or buildings, terminated at your network rack or closet. Every cable run is tested before handover.',
  },
  {
    question: 'Do you install the door hardware too, or do I need a locksmith?',
    answer: 'We install the electric door hardware for access control ourselves: electric strikes, maglocks and request-to-exit devices, along with the cabling, readers and controllers. We are not a locksmith. Mechanical keys and lock cylinders stay with your locksmith.',
  },
  {
    question: 'Which camera and access control systems do you install?',
    answer: 'For security cameras we install UniFi Protect. For door access we install UniFi Access and PDK. We are Certified UniFi installers, so the cameras, door access and Wi-Fi can run on one UniFi system if that suits your building.',
  },
  {
    question: 'Which cities do you serve?',
    answer: 'We are based at 1624 E Territory Dr in Meridian and work across the Treasure Valley: Meridian, Boise, Nampa and Eagle, plus Caldwell, Kuna, Star, Garden City and Middleton. Call (208) 328-4500 to talk about your site.',
  },
  {
    question: 'Do you do residential work?',
    answer: 'No. We do commercial work only: offices, shops, warehouses and other business buildings.',
  },
  {
    question: 'Do you also offer managed IT?',
    answer: 'Yes. Managed IT support is a secondary service we offer for businesses that want the same local team looking after their computers and network after the install.',
  },
];

export function HomepageFAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-20 bg-navy-950 border-t border-white/5">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
            Common Questions
          </h2>
          <p className="text-gray-300 text-center mb-12 text-lg">
            Quick answers to what Treasure Valley businesses ask us most.
          </p>

          <div className="space-y-3">
            {homepageFAQs.map((item, index) => (
              <div
                key={index}
                className="border border-white/5 rounded-2xl overflow-hidden bg-navy-800/30 transition-all duration-300 hover:border-white/10"
              >
                <button
                  onClick={() => toggle(index)}
                  className="w-full flex items-center justify-between p-6 text-left"
                  aria-expanded={openIndex === index}
                >
                  <span className="font-semibold text-lg text-white pr-8">{item.question}</span>
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${openIndex === index ? 'bg-cobalt-500 text-white' : 'bg-white/10 text-gray-400'}`}>
                    {openIndex === index ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                  </div>
                </button>
                <div
                  className="grid transition-all duration-200 ease-in-out"
                  style={{
                    gridTemplateRows: openIndex === index ? '1fr' : '0fr',
                    opacity: openIndex === index ? 1 : 0,
                  }}
                >
                  <div className="overflow-hidden">
                    <div className="px-6 pb-6 pt-0">
                      <p className="text-gray-300 leading-relaxed text-base border-t border-white/5 pt-4">
                        {item.answer}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link
              to="/faq"
              className="inline-flex items-center text-cobalt-400 hover:text-cobalt-300 font-medium transition-colors"
            >
              View All FAQ
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
